import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { API, POLL_MS, errMsg } from '../components/turnaround/taUtils';
import { TurnaroundHeader } from '../components/turnaround/TurnaroundHeader';
import { FlightGantt } from '../components/turnaround/FlightGantt';
import { ActivityTable } from '../components/turnaround/ActivityTable';
import { CorrectTimeDialog } from '../components/turnaround/CorrectTimeDialog';
import { DelayCauseDialog } from '../components/turnaround/DelayCauseDialog';
import { AuditTrail } from '../components/turnaround/AuditTrail';

export default function TurnaroundFlightPage() {
    const { flightId } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [view, setView] = useState(null);
    const [error, setError] = useState('');
    const [audit, setAudit] = useState([]);
    const [busy, setBusy] = useState(false);
    const [showPre, setShowPre] = useState(null);
    const [correcting, setCorrecting] = useState(null);
    const [delayTarget, setDelayTarget] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/turnaround/flight/${flightId}`, { headers });
            setView(res.data);
            setError('');
            setShowPre(prev => (prev === null ? res.data.settings?.show_pre_arrival !== false : prev));
            const au = await axios.get(`${API}/turnaround/records/${res.data.id}/audit`, { headers });
            setAudit(au.data);
        } catch (e) { setError(errMsg(e, 'No se pudo cargar el turnaround')); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flightId, token]);

    useEffect(() => { load(); }, [load]);
    useAutoRefresh(load, POLL_MS);

    const run = async (fn, okMsg) => {
        setBusy(true);
        try { await fn(); toast.success(okMsg); await load(); return true; }
        catch (e) { toast.error(errMsg(e)); return false; }
        finally { setBusy(false); }
    };
    const onAction = (a, mode) => run(() => axios.post(`${API}/turnaround/records/${view.id}/activities/${a.key}/${mode}`, {}, { headers }),
        mode === 'start' ? `Inicio registrado: ${a.name}` : mode === 'end' ? `Fin registrado: ${a.name}` : `Registrado: ${a.name}`);
    const onCorrect = async (payload) => {
        if (await run(() => axios.patch(`${API}/turnaround/records/${view.id}/activities/${correcting.key}/correct`, payload, { headers }), 'Hora corregida')) setCorrecting(null);
    };
    const onDelay = async (payload) => {
        if (await run(() => axios.post(`${API}/turnaround/records/${view.id}/delay`, payload, { headers }), 'Causa de demora registrada')) setDelayTarget(null);
    };

    if (error) return (
        <Card className="card-aviation"><CardContent className="p-10 text-center space-y-3" data-testid="ta-flight-error">
            <AlertTriangle className="w-10 h-10 mx-auto text-amber-700 dark:text-amber-400" /><p>{error}</p>
            <Button variant="outline" onClick={() => navigate('/turnaround')}><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button>
        </CardContent></Card>
    );
    if (!view) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

    return (
        <div className="space-y-5" data-testid="turnaround-flight-page">
            <div className="flex flex-wrap items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => navigate('/turnaround')} data-testid="ta-back"><ArrowLeft className="w-4 h-4 mr-1" />Turnarounds</Button>
                <span className="text-xs text-muted-foreground flex items-center gap-1.5"><RefreshCw className="w-3 h-3" />Se actualiza cada 15 s · Plantilla: {view.template_name}</span>
                <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                    <Switch checked={!!showPre} onCheckedChange={setShowPre} data-testid="ta-toggle-pre" />Actividades previas a la llegada
                </label>
            </div>
            <TurnaroundHeader view={view} />
            {view.exceeded && view.can_overall_delay && !view.delay_cause && (
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm" data-testid="ta-overall-delay-banner">
                    <AlertTriangle className="w-4 h-4 text-red-700 dark:text-red-400" />El turnaround excede el tiempo estándar.
                    <Button size="sm" variant="outline" className="ml-auto border-red-500/40" onClick={() => setDelayTarget({ overall: true })} data-testid="ta-overall-delay">Registrar causa</Button>
                </div>
            )}
            {view.delay_cause && <p className="text-sm text-red-700 dark:text-red-300" data-testid="ta-overall-cause">Causa del turnaround: {view.delay_cause.cause}{view.delay_cause.note ? ` — ${view.delay_cause.note}` : ''}</p>}
            <Card className="card-aviation"><CardContent className="p-4"><FlightGantt activities={view.activities} standard={view.standard_minutes} nowMin={view.status === 'en_curso' ? view.now_min : null} showPre={showPre} /></CardContent></Card>
            <ActivityTable view={view} busy={busy} onAction={onAction} onCorrect={setCorrecting} onDelay={(a) => setDelayTarget({ activity: a })} showPre={showPre} />
            <Card className="card-aviation"><CardContent className="p-4"><AuditTrail entries={audit} tz={view.timezone} /></CardContent></Card>
            <CorrectTimeDialog activity={correcting} tz={view.timezone} onClose={() => setCorrecting(null)} onSubmit={onCorrect} />
            <DelayCauseDialog target={delayTarget} causes={view.settings?.delay_causes} onClose={() => setDelayTarget(null)} onSubmit={onDelay} />
        </div>
    );
}
